/**
 * MSAL Cache Management Utilities
 * Provides synchronous cache clearing functions to prevent ProtectedRoute redirects
 */

import type { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';

/**
 * Clear MSAL account and cache synchronously
 * This prevents ProtectedRoute from redirecting before error screens can be shown.
 * Always clears in-memory active account and MSAL storage keys when instance is available,
 * even when account is null (e.g. after storage was already cleared), so tokens never persist after sign-out.
 *
 * @param msalInstance - MSAL public client application instance (IPublicClientApplication interface or compatible)
 * @param account - MSAL account info (can be null; clearing still runs when instance is available)
 */
export function clearMsalCacheSynchronously(
  msalInstance: IPublicClientApplication | null | any,
  _account: AccountInfo | null
): void {
  if (!msalInstance) {
    return;
  }

  // Type guard: ensure instance has required methods
  if (typeof msalInstance.setActiveAccount !== 'function' || typeof msalInstance.getConfiguration !== 'function') {
    return;
  }

  // CRITICAL: Always clear active account (even when account is null).
  // If storage was cleared first, getActiveAccount() can be null but in-memory state may still hold the account;
  // without this, tokens would still appear "available" after sign-out.
  msalInstance.setActiveAccount(null);

  // CRITICAL: Also clear all accounts from MSAL's internal cache
  // useIsAuthenticated() checks getAllAccounts().length > 0, so we must clear ALL accounts
  try {
    msalInstance.getAllAccounts();
  } catch {
    // Ignore - clearing will still proceed
  }

  // Clear MSAL cache from storage (both configured location and the other, for defense-in-depth).
  // Run even when account is null so we never leave stale msal.* keys after logout.
  try {
    const clientId = msalInstance.getConfiguration().auth.clientId;
    const cacheLocation = msalInstance.getConfiguration().cache?.cacheLocation || 'sessionStorage';
    const primaryStorage = cacheLocation === 'localStorage' ? window.localStorage : window.sessionStorage;
    const storages: Storage[] = [primaryStorage];
    // Clear from both storages so tokens never persist (e.g. if config uses localStorage, also clear sessionStorage msal keys)
    if (primaryStorage === window.localStorage) {
      storages.push(window.sessionStorage);
    } else {
      storages.push(window.localStorage);
    }

    for (const storage of storages) {
      const keysToRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key && (key.startsWith(`msal.${clientId}`) || key.startsWith('msal.'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => {
        try {
          storage.removeItem(key);
        } catch {
          // Ignore per-key failures
        }
      });
    }

    try {
      msalInstance.getAllAccounts();
    } catch {
      // Ignore - verification only
    }
  } catch {
    // Ignore - fail-open; storage may already be cleared
  }
}
